/**
 * Parallel Submission Planning View (A2F-444).
 *
 * Visual timeline showing all active submissions as horizontal lanes with
 * completion metrics, status badges, and conflict detection.
 */

import { useCallback, useState } from 'react';
import { parseDateOnly, formatDateOnly } from '@/lib/dateOnly';
import { useProject } from '@/lib/ProjectContext';
import { api } from '@/lib/api';
import { invalidateConsistency } from '@/lib/consistencyInvalidation';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { usePlanningData } from './usePlanningData';
import { TimelineGrid } from './TimelineGrid';
import { SubmissionLane } from './SubmissionLane';
import { ConflictWarning } from './ConflictWarning';
import type { Submission } from '@/types/api';

function computeTimelineRange(submissions: Submission[]): { start: Date; end: Date } {
  const now = new Date();
  let earliest = new Date(now.getFullYear(), now.getMonth() - 2, 1); // default 2 months ago
  let latest = new Date(now.getFullYear(), now.getMonth() + 3, 1); // default 3 months ahead

  for (const sub of submissions) {
    if (sub.startDate) {
      const d = parseDateOnly(sub.startDate);
      if (d && d < earliest) earliest = d;
    }
    if (sub.targetDate) {
      const d = parseDateOnly(sub.targetDate);
      if (d && d > latest) latest = d;
    }
  }

  // Round to month boundaries with buffer
  earliest = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
  latest = new Date(latest.getFullYear(), latest.getMonth() + 2, 1);

  return { start: earliest, end: latest };
}

export interface ConflictCluster {
  submissions: Submission[];
  earliest: Date;
  latest: Date;
  span: number;
}

function findConflictClusters(submissions: Submission[]): ConflictCluster[] {
  const withDates = submissions
    .filter(s => s.targetDate)
    .sort((a, b) => parseDateOnly(a.targetDate)!.getTime() - parseDateOnly(b.targetDate)!.getTime());

  const clusters: ConflictCluster[] = [];
  let i = 0;
  while (i < withDates.length) {
    const clusterSubs = [withDates[i]];
    let clusterEnd = parseDateOnly(withDates[i].targetDate)!;

    let j = i + 1;
    while (j < withDates.length) {
      const nextDate = parseDateOnly(withDates[j].targetDate)!;
      const gap = Math.ceil((nextDate.getTime() - clusterEnd.getTime()) / (1000 * 60 * 60 * 24));
      if (gap <= 7) {
        clusterSubs.push(withDates[j]);
        if (nextDate > clusterEnd) clusterEnd = nextDate;
        j++;
      } else {
        break;
      }
    }

    if (clusterSubs.length >= 2) {
      const earliest = parseDateOnly(clusterSubs[0].targetDate)!;
      clusters.push({ submissions: clusterSubs, earliest, latest: clusterEnd, span: Math.ceil((clusterEnd.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24)) });
    }
    i = j;
  }
  return clusters;
}

export function PlanningViewPage() {
  const { activeProject } = useProject();
  const { submissions, classifications, sectionOwners, metrics, loading, refresh } = usePlanningData();
  const projectId = activeProject?.id ?? '';
  const [overlayScanningId, setOverlayScanningId] = useState<string | null>(null);

  const handleOverlayScan = useCallback(async (submissionId: string) => {
    if (!projectId) return;
    setOverlayScanningId(submissionId);
    try {
      await Promise.allSettled([
        api.compoundCode.scan(projectId, undefined, true, submissionId),
        api.crossReferences.scan(projectId, undefined, submissionId),
        api.abbreviations.scan(projectId, submissionId),
        api.doseUnit.scan(projectId, submissionId),
      ]);
      refresh();
      invalidateConsistency(projectId);
    } finally {
      setOverlayScanningId(null);
    }
  }, [projectId, refresh]);

  const handleDatesChange = useCallback(async (submissionId: string, startDate: string, targetDate: string) => {
    if (!projectId) return;
    try {
      await api.projects.updateSubmission(projectId, submissionId, {
        start_date: startDate,
        target_date: targetDate,
      });
      refresh();
    } catch {
      // silently ignore for MVP
    }
  }, [projectId, refresh]);

  if (loading) return <LoadingSpinner message="Loading planning view..." />;

  if (submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <div className="text-center">
          <p className="text-sm font-medium">No submissions found</p>
          <p className="text-xs mt-1">Add an IND with submissions to get started.</p>
        </div>
      </div>
    );
  }

  // Filter to planning submissions only (A2F-450)
  const planningSubmissions = submissions.filter(s => s.isPlanning);


  // Sort order (A2F-526, revised): strictly by displaySequence ascending.
  // Submission numbers encode intended priority — when a future submission
  // ships ahead of an earlier one, the numbering re-sequences and the sort
  // naturally follows. One axis, aligned with sidebar picker and Section Map.
  const sorted = [...planningSubmissions].sort((a, b) => a.displaySequence - b.displaySequence);

  const { start: timelineStart, end: timelineEnd } = computeTimelineRange(sorted);
  const conflictClusters = findConflictClusters(sorted);

  // Nearest future target date
  const now = new Date();
  const nextTarget = sorted.find(s => { const d = parseDateOnly(s.targetDate); return d !== null && d >= now; });
  const nextTargetFmt = nextTarget?.targetDate
    ? formatDateOnly(nextTarget.targetDate, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const soSummary = sectionOwners?.summary ?? null;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-charcoal">Submission Plan</h1>
        <span className="text-sm text-slate-500">
          {activeProject?.name}
          {nextTargetFmt && <> &middot; Next target: {nextTargetFmt}</>}
        </span>
      </div>

      {/* Conflict warnings — one per cluster, above the chart */}
      {conflictClusters.length > 0 && (
        <div className="space-y-2 mb-3">
          {conflictClusters.map((cluster) => (
            <ConflictWarning key={cluster.submissions.map(s => s.id).join('|')} cluster={cluster} />
          ))}
        </div>
      )}

      {/* Timeline container */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Timeline axis */}
        <div className="px-4 pt-4">
          <TimelineGrid startDate={timelineStart} endDate={timelineEnd} />
        </div>

        {/* Submission lanes */}
        <div className="divide-y divide-slate-100">
          {sorted.map((sub) => (
            <SubmissionLane
              key={sub.id}
              submission={sub}
              classification={classifications.get(sub.id) ?? null}
              metrics={metrics.get(sub.id) ?? null}
              timelineStart={timelineStart}
              timelineEnd={timelineEnd}
              onDatesChange={handleDatesChange}
              onCompletionChange={refresh}
              onOverlayScan={handleOverlayScan}
              overlayScanningId={overlayScanningId}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
